import React, { createContext, useState, useEffect, useContext } from 'react';
import { ThemeProvider as MuiThemeProvider, createTheme } from '@mui/material/styles';

const ThemeContext = createContext();

const getThemeObject = (mode) =>
  createTheme({
    palette: {
      mode,
      primary: {
        main: '#1976d2',
      },
      secondary: {
        main: '#dc004e',
      },
      background: {
        default: mode === 'dark' ? '#18191a' : '#f4f6f8',
        paper: mode === 'dark' ? '#18191a' : '#fff',
      },
      text: {
        primary: mode === 'dark' ? '#fff' : '#18191a',
        secondary: mode === 'dark' ? '#c7c7c7' : '#333',
      },
    },
    components: {
      MuiDrawer: {
        styleOverrides: {
          paper: {
            backgroundColor: mode === 'dark' ? '#18191a' : '#fff',
            color: mode === 'dark' ? '#fff' : '#18191a',
          },
        },
      },
      MuiList: {
        styleOverrides: {
          root: {
            backgroundColor: 'inherit',
            color: 'inherit',
          },
        },
      },
      MuiListItem: {
        styleOverrides: {
          root: {
            color: mode === 'dark' ? '#fff' : '#18191a',
            backgroundColor: 'inherit',
            '&.Mui-selected, &.Mui-selected:hover': {
              backgroundColor: mode === 'dark' ? '#23272f' : '#e3e3e3',
            },
            '&:hover': {
              backgroundColor: mode === 'dark' ? '#23272f' : '#f5f5f5',
            },
          },
        },
      },
      MuiListItemIcon: {
        styleOverrides: {
          root: {
            color: mode === 'dark' ? '#fff' : undefined,
          },
        },
      },
      MuiListItemText: {
        styleOverrides: {
          primary: {
            color: mode === 'dark' ? '#fff' : '#18191a',
          },
        },
      },
      MuiMenu: {
        styleOverrides: {
          paper: {
            backgroundColor: mode === 'dark' ? '#23272f' : '#fff',
            color: mode === 'dark' ? '#fff' : '#18191a',
          },
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            color: mode === 'dark' ? '#fff' : '#18191a',
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundColor: mode === 'dark' ? '#18191a' : '#fff',
            color: mode === 'dark' ? '#fff' : '#18191a',
          },
        },
      },
      MuiSelect: {
        styleOverrides: {
          icon: {
            color: mode === 'dark' ? '#fff' : undefined,
          },
        },
      },
      MuiInputLabel: {
        styleOverrides: {
          root: {
            color: mode === 'dark' ? '#fff' : undefined,
          },
        },
      },
    },
  });

export const ThemeProvider = ({ children }) => {
  const [themeMode, setThemeMode] = useState(() => {
    const savedTheme = localStorage.getItem('theme');
    return savedTheme ? savedTheme : 'light';
  });

  useEffect(() => {
    document.body.className = themeMode;
    localStorage.setItem('theme', themeMode);
  }, [themeMode]);

  const toggleTheme = () => {
    setThemeMode((prev) => (prev === 'light' ? 'dark' : 'light'));
  };

  const setTheme = (mode) => {
    setThemeMode(mode);
  };

  const theme = getThemeObject(themeMode);

  return (
    <ThemeContext.Provider value={{ themeMode, toggleTheme, setTheme }}>
      <MuiThemeProvider theme={theme}>
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
};

export const useTheme = () => useContext(ThemeContext);
